// Para Agent inside the Hub (SUP 01 to SUP 04).
//
// The agent itself is the existing Copilot Studio agent, set to "Authenticate
// with Microsoft" so that it also works in Teams. This file only puts it on
// the page. The page reaches the agent with Microsoft's Copilot Studio client
// (Microsoft 365 Agents SDK) as the signed-in staff member: it takes a token
// for Copilot Studio silently from the session they already signed in with,
// so there is no second sign-in and no secret. Microsoft's Web Chat bundle,
// from the pinned botframework-webchat package, draws the conversation.

// The delegated Power Platform API permission the Hub registration holds.
export const agentScope = 'https://api.powerplatform.com/CopilotStudio.Copilots.Invoke';

// Para Agent's environment and schema name, from Copilot Studio (Settings,
// Advanced, Metadata). Neither is a secret. See RUNBOOK.md.
export interface AgentConfig {
  environmentId: string;
  schemaName: string;
}

export function readAgentConfig(env: Record<string, string | undefined>): AgentConfig | null {
  const environmentId = env.VITE_AGENT_ENVIRONMENT_ID?.trim();
  const schemaName = env.VITE_AGENT_SCHEMA_NAME?.trim();
  if (!environmentId || !schemaName) return null;
  return { environmentId, schemaName };
}

interface Observer<T> {
  next?(value: T): void;
  error?(err: unknown): void;
  complete?(): void;
}

interface Subscription {
  unsubscribe(): void;
}

interface Observable<T> {
  subscribe(observer: Observer<T>): Subscription;
}

// The connection the Copilot Studio client gives Web Chat in place of Direct
// Line. Its status is 0 (not connected), 1 (connecting) or 2 (connected).
export interface AgentConnection {
  activity$: Observable<unknown>;
  connectionStatus$: Observable<number>;
  postActivity(activity: unknown): unknown;
  end(): void;
  readonly conversationId: string | undefined;
}

const CONNECTED = 2;

// How long the conversation may take to start before the support route shows.
export const CONNECT_TIMEOUT_MS = 30_000;

// The parts of @microsoft/agents-copilotstudio-client the page uses.
export interface AgentClientModule {
  CopilotStudioClient: new (settings: AgentConfig, token: string) => object;
  CopilotStudioWebChat: { createConnection(client: object, settings: { showTyping: boolean }): AgentConnection };
}

// Web Chat subscribes to activity$ for messages only, so a conversation that
// fails to start would leave it on "Connecting…" for good. Each subscription
// Web Chat makes is passed straight through, one for one, and an error there
// also shows the support route. Nothing else subscribes to activity$: the
// client starts the conversation on its first subscription.
export function guardConnection(connection: AgentConnection, onError: () => void): AgentConnection {
  return {
    activity$: {
      subscribe: (observer: Observer<unknown> | ((value: unknown) => void)) => {
        const target = typeof observer === 'function' ? { next: observer } : observer;
        return connection.activity$.subscribe({
          next: (value) => target.next?.(value),
          error: (err) => {
            onError();
            target.error?.(err);
          },
          complete: () => target.complete?.(),
        });
      },
    },
    connectionStatus$: connection.connectionStatus$,
    postActivity: (activity) => connection.postActivity(activity),
    end: () => connection.end(),
    get conversationId() {
      return connection.conversationId;
    },
  };
}

interface WebChatGlobal {
  renderWebChat(props: Record<string, unknown>, element: HTMLElement): void;
}

declare global {
  interface Window {
    WebChat?: WebChatGlobal;
  }
}

function loadWebChat(src = '/webchat.js'): Promise<WebChatGlobal> {
  if (window.WebChat) return Promise.resolve(window.WebChat);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => (window.WebChat ? resolve(window.WebChat) : reject(new Error('Web Chat did not load.')));
    script.onerror = () => reject(new Error('Web Chat did not load.'));
    document.head.appendChild(script);
  });
}

// Its own chunk, fetched only when the chat is first opened.
const loadAgentClient = (): Promise<AgentClientModule> => import('@microsoft/agents-copilotstudio-client');

export interface MountDeps {
  config: AgentConfig | null;
  // A Copilot Studio token for the signed-in staff member, or null when one
  // cannot be had without a prompt.
  fetchToken: () => Promise<string | null>;
  userName: string;
  fallbackHtml: string;
  loader?: () => Promise<WebChatGlobal>;
  clientLoader?: () => Promise<AgentClientModule>;
  timeoutMs?: number;
  // Aborted when the chat is started again. The connection is ended and
  // nothing more is written to the element.
  signal?: AbortSignal;
}

export async function mountAgent(element: HTMLElement, deps: MountDeps): Promise<boolean> {
  if (deps.signal?.aborted) return false;
  let connection: AgentConnection | undefined;
  let watch: Subscription | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let ended = false;
  const stop = () => {
    if (ended) return;
    ended = true;
    if (timer !== undefined) clearTimeout(timer);
    watch?.unsubscribe();
    connection?.end();
  };
  // SUP 04: when the agent cannot be reached, show the support route.
  const showFallback = () => {
    if (ended) return;
    stop();
    element.innerHTML = deps.fallbackHtml;
  };
  deps.signal?.addEventListener('abort', stop, { once: true });

  try {
    const config = deps.config;
    if (!config) throw new Error('Para Agent is not set up in this build.');
    const [token, WebChat, client] = await Promise.all([
      deps.fetchToken(),
      (deps.loader ?? loadWebChat)(),
      (deps.clientLoader ?? loadAgentClient)(),
    ]);
    if (ended) return false;
    if (!token) throw new Error('No Copilot Studio token without a prompt.');
    const agent = new client.CopilotStudioClient({ environmentId: config.environmentId, schemaName: config.schemaName }, token);
    const line = client.CopilotStudioWebChat.createConnection(agent, { showTyping: true });
    connection = line;
    // The conversation can also stall without an error, so it has a time limit.
    timer = setTimeout(showFallback, deps.timeoutMs ?? CONNECT_TIMEOUT_MS);
    watch = line.connectionStatus$.subscribe({
      next: (status) => {
        if (status === CONNECTED && timer !== undefined) {
          clearTimeout(timer);
          timer = undefined;
        }
      },
    });
    WebChat.renderWebChat(
      {
        directLine: guardConnection(line, showFallback),
        username: deps.userName,
        locale: 'en-AU',
        styleOptions: { hideUploadButton: true },
      },
      element,
    );
    return !ended;
  } catch {
    showFallback();
    return false;
  }
}
